import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Group as PanelGroup, Panel } from 'react-resizable-panels';
import { ChevronDown, ChevronRight, Upload, Trash2, Check, X } from 'lucide-react';
import Papa from 'papaparse';
import { useAuth } from '../../components/auth/AuthContext';
import ResizeHandle from '../../components/common/ResizeHandle';
import { cashflowService, SimulateRequest } from '../../services/api/cashflowService';
import SystemDateDisplay from '../../components/common/SystemDateDisplay';
import { useSystemDate } from '../../hooks/useSystemDate';
import { Cashflow, CashflowSummary, FlowDirection, ManualCashflow, BankAccountRef, ReferenceRatesData } from '../../types/cashflow';
import { CashflowBarChart, CashflowComboChart, CashflowSankeyChart } from '../../components/cashflow/CashflowCharts';
import Toast from '../../components/common/Toast';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../styles/TradeBooking.css';
import '../../styles/App.css';

// localStorage keys
const STARTING_BALANCES_KEY = 'cashflow_starting_balances_v2'; // v2: keyed by bank account string
// Note: Manual entries are now stored in Firestore, not localStorage

// Type for simplified starting balances (keyed by bank account string)
interface BankAccountBalances {
  [bankAccountKey: string]: number; // e.g., "Citi ****4521": 12345.78
}

// Helper: Generate UUID
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Helper: Parse date from various formats to DD-MM-YYYY
const parseAndNormalizeDate = (dateStr: string): string | null => {
  if (!dateStr || typeof dateStr !== 'string') return null;

  const trimmed = dateStr.trim();
  let day: number, month: number, year: number;

  // Try DD-MM-YYYY or DD/MM/YYYY
  let match = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (match) {
    day = parseInt(match[1], 10);
    month = parseInt(match[2], 10);
    year = parseInt(match[3], 10);
  } else {
    // Try YYYY-MM-DD
    match = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
    if (match) {
      year = parseInt(match[1], 10);
      month = parseInt(match[2], 10);
      day = parseInt(match[3], 10);
    } else {
      // Try MM/DD/YYYY (US format)
      match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (match) {
        month = parseInt(match[1], 10);
        day = parseInt(match[2], 10);
        year = parseInt(match[3], 10);
      } else {
        return null;
      }
    }
  }

  // Validate date components
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  // Return in DD-MM-YYYY format
  return `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
};

// Helper: Parse DD-MM-YYYY to Date object
const parseDDMMYYYYToDate = (dateStr: string): Date | null => {
  const match = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) return null;
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // JS months are 0-indexed
  const year = parseInt(match[3], 10);
  return new Date(year, month, day);
};

// Helper: Check if date is in the past
const isDateInPast = (dateStr: string): boolean => {
  const date = parseDDMMYYYYToDate(dateStr);
  if (!date) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

// Helper: Check if date is more than 30 days in the future
const isDateBeyondHorizon = (dateStr: string, maxDays: number = 30): boolean => {
  const date = parseDDMMYYYYToDate(dateStr);
  if (!date) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + maxDays);
  return date > maxDate;
};

// Helper: Parse bank account string to BankAccountRef
const parseBankAccountString = (str: string): BankAccountRef | undefined => {
  if (!str || typeof str !== 'string') return undefined;
  const trimmed = str.trim();
  if (!trimmed) return undefined;

  // Expected format: "BankName ****XXXX" or just "BankName"
  const match = trimmed.match(/^(.+?)\s+(\*{4}\d{4})$/);
  if (match) {
    return { bankName: match[1].trim(), accountNumber: match[2] };
  }
  // If no masked number, use the whole string as bank name
  return { bankName: trimmed, accountNumber: '****0000' };
};

const CashflowManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const agGridLocale = useMemo(() => {
    const lang = i18n.language?.substring(0, 2);
    if (lang === 'es') return AG_GRID_LOCALE_ES;
    if (lang === 'pt') return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);
  const gridRef = useRef<AgGridReact>(null);

  // State
  const [cashflows, setCashflows] = useState<Cashflow[]>([]);
  const [summary, setSummary] = useState<CashflowSummary | null>(null);
  const [referenceRates, setReferenceRates] = useState<ReferenceRatesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [daysAhead, setDaysAhead] = useState<number>(30); // Slider display value
  const [appliedDays, setAppliedDays] = useState<number>(30); // Value used for API calls

  const [includeProjected, setIncludeProjected] = useState<boolean>(true);
  const [currencyFilters, setCurrencyFilters] = useState<Set<string>>(new Set());
  const [directionFilter, setDirectionFilter] = useState<FlowDirection | ''>('');
  const [buySellFilter, setBuySellFilter] = useState<'Buy' | 'Sell' | ''>('');
  const [bankAccountFilters, setBankAccountFilters] = useState<Set<string>>(new Set());
  const [counterpartyFilters, setCounterpartyFilters] = useState<Set<string>>(new Set());
  const [ourPartyFilters, setOurPartyFilters] = useState<Set<string>>(new Set());
  const [settlementTypeFilters, setSettlementTypeFilters] = useState<Set<string>>(new Set());
  const [availableCurrencies, setAvailableCurrencies] = useState<string[]>([]);
  const [availableBankAccounts, setAvailableBankAccounts] = useState<string[]>([]);
  const [availableCounterparties, setAvailableCounterparties] = useState<string[]>([]);
  const [availableOurParties, setAvailableOurParties] = useState<string[]>([]);
  const [availableSettlementTypes, setAvailableSettlementTypes] = useState<string[]>([]);

  // Toggle helpers for multi-select filters
  const toggleCurrencyFilter = (currency: string) => {
    setCurrencyFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(currency)) {
        newSet.delete(currency);
      } else {
        newSet.add(currency);
      }
      return newSet;
    });
  };

  const toggleBankAccountFilter = (bankAccount: string) => {
    setBankAccountFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bankAccount)) {
        newSet.delete(bankAccount);
      } else {
        newSet.add(bankAccount);
      }
      return newSet;
    });
  };

  const toggleCounterpartyFilter = (counterparty: string) => {
    setCounterpartyFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(counterparty)) {
        newSet.delete(counterparty);
      } else {
        newSet.add(counterparty);
      }
      return newSet;
    });
  };

  const toggleOurPartyFilter = (party: string) => {
    setOurPartyFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(party)) newSet.delete(party);
      else newSet.add(party);
      return newSet;
    });
  };

  const toggleSettlementTypeFilter = (st: string) => {
    setSettlementTypeFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(st)) newSet.delete(st);
      else newSet.add(st);
      return newSet;
    });
  };

  // Payment filter state: '' = all, 'paid' = paid only, 'unpaid' = unpaid only
  const [paidFilter, setPaidFilter] = useState<'' | 'paid' | 'unpaid'>('');

  // Mark-paid modal state
  const [markPaidCashflow, setMarkPaidCashflow] = useState<Cashflow | null>(null);
  const [markPaidDate, setMarkPaidDate] = useState<string>('');
  const [markPaidNotes, setMarkPaidNotes] = useState<string>('');
  const [markPaidLoading, setMarkPaidLoading] = useState(false);

  // Rate simulation state — ephemeral overrides for what-if analysis
  const [rateOverrides, setRateOverrides] = useState<Record<string, Record<string, number>>>({});
  const [isSimulating, setIsSimulating] = useState(false);
  const [editingCell, setEditingCell] = useState<{ rateType: string; date: string } | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [shiftPopover, setShiftPopover] = useState<string | null>(null); // rateType currently showing shift controls
  const [customShiftValue, setCustomShiftValue] = useState('');
  const [customShiftMode, setCustomShiftMode] = useState<'percent' | 'absolute'>('percent');

  const hasOverrides = Object.keys(rateOverrides).length > 0;

  // True when simulation results are currently displayed (backend returned 'override' sources)
  const isSimulationActive = useMemo(() => {
    if (!referenceRates?.sources) return false;
    return Object.values(referenceRates.sources).some(
      daySources => Object.values(daySources).includes('override')
    );
  }, [referenceRates]);

  // Chart accordion — which chart is expanded in Vista Gráfica (null = all collapsed as spines)
  const [expandedChart, setExpandedChart] = useState<string | null>('bar');
  const toggleExpandedChart = (chartId: string) => {
    setExpandedChart(prev => prev === chartId ? null : chartId);
  };

  // Chart section heights (per currency) — resizable via drag handle
  const DEFAULT_CHART_HEIGHT = 310;
  const [chartHeights, setChartHeights] = useState<Record<string, number>>({});
  const dragRef = useRef<{ currency: string; startY: number; startHeight: number } | null>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const delta = e.clientY - dragRef.current.startY;
      const newHeight = Math.max(200, Math.min(800, dragRef.current.startHeight + delta));
      setChartHeights(prev => ({
        ...prev,
        [dragRef.current!.currency]: newHeight,
      }));
    };
    const handleMouseUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  // Starting balances state - keyed by bank account string, persisted to localStorage
  const [bankAccountBalances, setBankAccountBalances] = useState<BankAccountBalances>(() => {
    const saved = localStorage.getItem(STARTING_BALANCES_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return {};
      }
    }
    return {};
  });

  // Context menu state for bank account balance editing
  const [balanceMenuAnchor, setBalanceMenuAnchor] = useState<{ x: number; y: number; bankAccount: string } | null>(null);
  const [editingBalance, setEditingBalance] = useState<string>('');

  // Manual cashflows state - loaded from Firestore via API
  const [manualCashflows, setManualCashflows] = useState<ManualCashflow[]>([]);
  const manualGridRef = useRef<AgGridReact>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Persist bank account balances to localStorage
  useEffect(() => {
    localStorage.setItem(STARTING_BALANCES_KEY, JSON.stringify(bankAccountBalances));
  }, [bankAccountBalances]);

  // Manual cashflows are now stored in Firestore - no localStorage persistence needed

  // Collapsed sections state (by currency) - persisted to localStorage
  const COLLAPSED_SECTIONS_KEY = 'cashflow_collapsed_sections';
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch {
        return new Set();
      }
    }
    return new Set();
  });
  const initialLoadDone = useRef(false);
  const hasPersistedState = useRef(!!localStorage.getItem(COLLAPSED_SECTIONS_KEY));

  const clientId = user?.organization?.id;
  const systemDateResult = useSystemDate(clientId);

  // Helper to sort currencies: CLP first, USD second, then alphabetically
  const sortCurrencies = (currencies: string[]): string[] => {
    return [...currencies].sort((a, b) => {
      if (a === 'CLP') return -1;
      if (b === 'CLP') return 1;
      if (a === 'USD') return -1;
      if (b === 'USD') return 1;
      return a.localeCompare(b);
    });
  };

  // Toggle section collapse and persist to localStorage
  const toggleSection = (currency: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(currency)) {
        newSet.delete(currency);
      } else {
        newSet.add(currency);
      }
      // Persist to localStorage
      localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(Array.from(newSet)));
      return newSet;
    });
  };

  // Load cashflows
  const loadCashflows = useCallback(async () => {
    if (!clientId) {
      setError(t('cashflow.messages.noClientId', 'Client ID not found'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await cashflowService.getCashflows(clientId, {
        days: appliedDays,
        includePending: true,
        // Currency and bank account filters are applied client-side for multi-select
        direction: directionFilter || undefined,
        startDate: systemDateResult.systemDate || undefined,
      });

      if (response.success && response.data) {
        // Separate trade cashflows from manual cashflows
        const allCashflows = response.data.cashflows;
        const tradeCashflows = allCashflows.filter((cf: any) => cf.source !== 'manual');
        const manualCfs = allCashflows.filter((cf: any) => cf.source === 'manual');

        setCashflows(tradeCashflows);
        setManualCashflows(manualCfs.map((cf: any) => ({
          id: cf.id,
          description: cf.description || '',
          currency: cf.currency,
          amount: cf.amount,
          flowDirection: cf.flowDirection,
          expectedDate: cf.expectedDate,
          bankAccount: cf.bankAccount,
          uploadedAt: cf.uploadedAt || cf.createdAt,
          source: 'manual' as const,
        })));
        setSummary(response.data.summary);
        setReferenceRates((response.data as any).referenceRates || null);
      } else {
        setError(response.message || t('cashflow.messages.error', 'Error loading cashflows'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('cashflow.messages.error', 'Error loading cashflows'));
    } finally {
      setLoading(false);
    }
  }, [clientId, appliedDays, directionFilter, systemDateResult.systemDate, t]);

  // Run simulation with current rate overrides
  const runSimulation = useCallback(async () => {
    if (!clientId || !hasOverrides) return;

    try {
      setIsSimulating(true);
      setError(null);

      const request: SimulateRequest = {
        rateOverrides,
        days: appliedDays,
        startDate: systemDateResult.systemDate || undefined,
      };

      const response = await cashflowService.simulateCashflows(clientId, request);

      if (response.success && response.data) {
        const allCashflows = response.data.cashflows;
        const tradeCashflows = allCashflows.filter((cf: any) => cf.source !== 'manual');
        const manualCfs = allCashflows.filter((cf: any) => cf.source === 'manual');

        setCashflows(tradeCashflows);
        setManualCashflows(manualCfs.map((cf: any) => ({
          id: cf.id,
          description: cf.description || '',
          currency: cf.currency,
          amount: cf.amount,
          flowDirection: cf.flowDirection,
          expectedDate: cf.expectedDate,
          bankAccount: cf.bankAccount,
          uploadedAt: cf.uploadedAt || cf.createdAt,
          source: 'manual' as const,
        })));
        setSummary(response.data.summary);
        setReferenceRates((response.data as any).referenceRates || null);

        setToastMessage(t('cashflow.referenceRates.simulationApplied', 'Simulation applied'));
        setToastType('info');
        setShowToast(true);

        // Flash projected rows in the AG Grid after React renders
        setTimeout(() => {
          const api = gridRef.current?.api;
          if (api) {
            const projectedNodes: any[] = [];
            api.forEachNode((node: any) => {
              if (node.data?.isProjected) {
                projectedNodes.push(node);
              }
            });
            if (projectedNodes.length > 0) {
              api.flashCells({
                rowNodes: projectedNodes,
                columns: ['amount'],
                flashDuration: 1500,
                fadeDuration: 500,
              });
            }
          }
        }, 100);
      } else {
        setError(response.message || t('cashflow.messages.error', 'Error'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('cashflow.messages.error', 'Error'));
    } finally {
      setIsSimulating(false);
    }
  }, [clientId, hasOverrides, rateOverrides, appliedDays, systemDateResult.systemDate, t]);

  // Reset overrides and reload original data
  const resetSimulation = useCallback(() => {
    setRateOverrides({});
    setEditingCell(null);
    // Trigger a fresh load of the original data
    loadCashflows();
  }, [loadCashflows]);

  // Load available currencies
  const loadCurrencies = useCallback(async () => {
    if (!clientId) return;

    const response = await cashflowService.getAvailableCurrencies(clientId);
    if (response.success && response.data) {
      setAvailableCurrencies(response.data);
    }
  }, [clientId]);

  // Map bank account to currency (derived from cashflows)
  const bankAccountToCurrency = useMemo(() => {
    const map: Record<string, string> = {};
    cashflows.forEach((cf) => {
      if (cf.bankAccount) {
        const key = `${cf.bankAccount.bankName} ${cf.bankAccount.accountNumber}`;
        if (!map[key]) {
          map[key] = cf.currency;
        }
      }
    });
    return map;
  }, [cashflows]);

  // Get starting balance for a specific currency
  // If bank account filters are applied, only sum balances from filtered accounts
  // Otherwise, sum all bank accounts for that currency
  const getStartingBalanceForCurrency = useCallback((currency: string): number => {
    let total = 0;
    Object.entries(bankAccountBalances).forEach(([bankAccount, balance]) => {
      // Check if this bank account matches the currency
      if (bankAccountToCurrency[bankAccount] !== currency) {
        return;
      }
      // If bank account filters are applied, only include filtered accounts
      if (bankAccountFilters.size > 0 && !bankAccountFilters.has(bankAccount)) {
        return;
      }
      total += balance;
    });
    return total;
  }, [bankAccountBalances, bankAccountToCurrency, bankAccountFilters]);

  // Format number with thousand separators for display (no forced decimals)
  const formatBalanceForDisplay = (value: number): string => {
    // Check if the number has decimal places
    const hasDecimals = value % 1 !== 0;
    return value.toLocaleString('en-US', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    });
  };

  // Parse formatted balance string back to number
  const parseBalanceFromEdit = (str: string): number => {
    // Remove commas and parse
    const cleaned = str.replace(/,/g, '');
    return parseFloat(cleaned) || 0;
  };

  // Handle balance input change - just store raw input, format on blur
  const handleBalanceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow typing freely - only restrict to valid number characters
    const rawValue = e.target.value;
    // Allow digits, commas (for pasting formatted numbers), decimal point, and minus
    const cleaned = rawValue.replace(/[^0-9,.-]/g, '');
    setEditingBalance(cleaned);
  };

  // Format on blur for cleaner display
  const handleBalanceInputBlur = () => {
    const numValue = parseBalanceFromEdit(editingBalance);
    if (!isNaN(numValue) && editingBalance !== '') {
      setEditingBalance(formatBalanceForDisplay(numValue));
    }
  };

  // Handle right-click on bank account filter button
  const handleBankAccountContextMenu = (e: React.MouseEvent, bankAccount: string) => {
    e.preventDefault();
    setBalanceMenuAnchor({ x: e.clientX, y: e.clientY, bankAccount });
    const balance = bankAccountBalances[bankAccount] || 0;
    // If balance is 0, show empty field so user can start typing immediately
    setEditingBalance(balance === 0 ? '' : formatBalanceForDisplay(balance));
  };

  // Save the edited balance
  const saveBalance = () => {
    if (balanceMenuAnchor) {
      const value = parseBalanceFromEdit(editingBalance);
      setBankAccountBalances(prev => ({
        ...prev,
        [balanceMenuAnchor.bankAccount]: value,
      }));
      setBalanceMenuAnchor(null);
    }
  };

  // Close balance menu
  const closeBalanceMenu = () => {
    setBalanceMenuAnchor(null);
  };

  // CSV Upload handling
  const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const newCashflows: ManualCashflow[] = [];
        const errors: string[] = [];
        let skippedPastDates = 0;
        let skippedFutureDates = 0;

        results.data.forEach((row: any, index: number) => {
          const rowNum = index + 2; // +2 for header row and 1-based indexing

          // Parse date
          const dateStr = row.Date || row.date || row.DATE;
          const normalizedDate = parseAndNormalizeDate(dateStr);
          if (!normalizedDate) {
            errors.push(`Row ${rowNum}: Invalid date "${dateStr}"`);
            return;
          }

          // Check if date is in the past
          if (isDateInPast(normalizedDate)) {
            skippedPastDates++;
            return;
          }

          // Check if date is beyond 30 days
          if (isDateBeyondHorizon(normalizedDate, 30)) {
            skippedFutureDates++;
            return;
          }

          // Parse currency
          const currency = (row.Currency || row.currency || row.CURRENCY || '').toUpperCase().trim();
          if (!currency || !/^[A-Z]{3}$/.test(currency)) {
            errors.push(`Row ${rowNum}: Invalid currency "${currency}"`);
            return;
          }

          // Parse amount
          const amountStr = row.Amount || row.amount || row.AMOUNT;
          const amount = parseFloat(String(amountStr).replace(/,/g, ''));
          if (isNaN(amount)) {
            errors.push(`Row ${rowNum}: Invalid amount "${amountStr}"`);
            return;
          }

          // Determine direction from amount sign or explicit Direction column
          let direction: FlowDirection;
          const directionStr = (row.Direction || row.direction || row.DIRECTION || '').toLowerCase().trim();
          if (directionStr === 'in' || directionStr === 'inbound') {
            direction = 'inbound';
          } else if (directionStr === 'out' || directionStr === 'outbound') {
            direction = 'outbound';
          } else {
            // Determine from amount sign
            direction = amount >= 0 ? 'inbound' : 'outbound';
          }

          // Parse description
          const description = (row.Description || row.description || row.DESCRIPTION || '').trim();
          if (!description) {
            errors.push(`Row ${rowNum}: Missing description`);
            return;
          }

          // Parse bank account
          const bankAccountStr = row.BankAccount || row.bankAccount || row.Bank || row.bank || '';
          const bankAccount = parseBankAccountString(bankAccountStr);

          // Create manual cashflow
          const manualCashflow: ManualCashflow = {
            id: generateUUID(),
            description,
            currency,
            amount: Math.abs(amount),
            flowDirection: direction,
            expectedDate: normalizedDate,
            bankAccount,
            uploadedAt: new Date().toISOString(),
            source: 'manual',
          };

          newCashflows.push(manualCashflow);
        });

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // Show results
        if (errors.length > 0) {
          setToastMessage(t('cashflow.manual.invalidFormat', 'CSV validation errors:\n') + errors.slice(0, 5).join('\n') + (errors.length > 5 ? `\n...and ${errors.length - 5} more` : ''));
          setToastType('error');
          setShowToast(true);
          return;
        }

        const totalSkipped = skippedPastDates + skippedFutureDates;

        if (newCashflows.length === 0 && totalSkipped > 0) {
          // All entries skipped
          const reasons: string[] = [];
          if (skippedPastDates > 0) reasons.push(t('cashflow.manual.reasonPast', { count: skippedPastDates, defaultValue: `${skippedPastDates} in past` }));
          if (skippedFutureDates > 0) reasons.push(t('cashflow.manual.reasonFuture', { count: skippedFutureDates, defaultValue: `${skippedFutureDates} beyond 30 days` }));
          setToastMessage(t('cashflow.manual.allSkipped', { count: totalSkipped, reasons: reasons.join(', '), defaultValue: `All ${totalSkipped} entries skipped (${reasons.join(', ')})` }));
          setToastType('warning');
          setShowToast(true);
          return;
        }

        // Save to Firestore via API
        if (!clientId) {
          setToastMessage(t('cashflow.messages.noClientId', 'Client ID not found'));
          setToastType('error');
          setShowToast(true);
          return;
        }

        // Convert to API format (date from DD-MM-YYYY to YYYY-MM-DD)
        const apiCashflows = newCashflows.map(cf => {
          // Convert DD-MM-YYYY to YYYY-MM-DD
          const dateParts = cf.expectedDate.split('-');
          const isoDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
          return {
            amount: cf.amount,
            currency: cf.currency,
            flowDirection: cf.flowDirection,
            expectedDate: isoDate,
            description: cf.description,
            bankAccount: cf.bankAccount,
            workflowState: 'Final',
          };
        });

        // Call batch API
        cashflowService.createCashflowsBatch(clientId, apiCashflows)
          .then(async (response) => {
            if (response.success) {
              // Reload cashflows to get the new data
              await loadCashflows();

              // Show success message
              if (totalSkipped > 0) {
                const reasons: string[] = [];
                if (skippedPastDates > 0) reasons.push(t('cashflow.manual.reasonPast', { count: skippedPastDates, defaultValue: `${skippedPastDates} in past` }));
                if (skippedFutureDates > 0) reasons.push(t('cashflow.manual.reasonFuture', { count: skippedFutureDates, defaultValue: `${skippedFutureDates} beyond 30 days` }));
                setToastMessage(t('cashflow.manual.uploadPartialSuccess', { count: newCashflows.length, skipped: totalSkipped, reasons: reasons.join(', '), defaultValue: `${newCashflows.length} entries imported. ${totalSkipped} skipped (${reasons.join(', ')}).` }));
                setToastType('warning');
              } else {
                setToastMessage(t('cashflow.manual.uploadSuccess', { count: newCashflows.length, defaultValue: `${newCashflows.length} entries imported successfully.` }));
                setToastType('success');
              }
              setShowToast(true);
            } else {
              setToastMessage(response.message || t('cashflow.manual.uploadError', 'Failed to save cashflows'));
              setToastType('error');
              setShowToast(true);
            }
          })
          .catch((err) => {
            setToastMessage(err.message || t('cashflow.manual.uploadError', 'Failed to save cashflows'));
            setToastType('error');
            setShowToast(true);
          });
      },
      error: (error) => {
        setToastMessage(t('cashflow.manual.invalidFormat', `Failed to parse CSV: ${error.message}`));
        setToastType('error');
        setShowToast(true);
      },
    });
  };

  // Delete a manual cashflow
  const deleteManualCashflow = async (id: string) => {
    if (!clientId) return;

    try {
      const response = await cashflowService.deleteCashflow(clientId, id);
      if (response.success) {
        // Update local state immediately for responsiveness
        setManualCashflows(prev => prev.filter(cf => cf.id !== id));
        setToastMessage(t('cashflow.manual.deleted', 'Entry deleted.'));
        setToastType('success');
        setShowToast(true);
      } else {
        setToastMessage(response.message || t('cashflow.manual.deleteError', 'Failed to delete entry'));
        setToastType('error');
        setShowToast(true);
      }
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : t('cashflow.manual.deleteError', 'Failed to delete entry'));
      setToastType('error');
      setShowToast(true);
    }
  };

  // Clear all manual cashflows
  const clearAllManualCashflows = async () => {
    if (!clientId) return;

    try {
      const response = await cashflowService.deleteAllManualCashflows(clientId);
      if (response.success) {
        // Update local state immediately
        setManualCashflows([]);
        setToastMessage(t('cashflow.manual.cleared', `All ${response.data.deletedCount} manual entries cleared.`));
        setToastType('info');
        setShowToast(true);
      } else {
        setToastMessage(response.message || t('cashflow.manual.clearError', 'Failed to clear entries'));
        setToastType('error');
        setShowToast(true);
      }
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : t('cashflow.manual.clearError', 'Failed to clear entries'));
      setToastType('error');
      setShowToast(true);
    }
  };

  // Open mark-paid modal
  const openMarkPaidModal = (cashflow: Cashflow) => {
    setMarkPaidCashflow(cashflow);
    if (cashflow.actualPaymentDate) {
      // Already paid — show details
      setMarkPaidDate(cashflow.actualPaymentDate);
      setMarkPaidNotes(cashflow.paymentNotes || '');
    } else {
      // Default to system date (fall back to browser clock)
      setMarkPaidDate(systemDateResult.systemDate || new Date().toISOString().slice(0, 10));
      setMarkPaidNotes('');
    }
  };

  const closeMarkPaidModal = () => {
    setMarkPaidCashflow(null);
    setMarkPaidDate('');
    setMarkPaidNotes('');
  };

  // Mark cashflow as paid
  const handleMarkPaid = async () => {
    if (!clientId || !markPaidCashflow || !markPaidDate) return;
    setMarkPaidLoading(true);
    try {
      const cfId = (markPaidCashflow as any).id;
      const response = await cashflowService.markCashflowPaid(clientId, cfId, {
        actualPaymentDate: markPaidDate,
        paymentNotes: markPaidNotes || undefined,
      });
      if (response.success) {
        // Update local state
        setCashflows(prev => prev.map(cf =>
          (cf as any).id === cfId
            ? { ...cf, actualPaymentDate: markPaidDate, paymentMarkedAt: new Date().toISOString(), paymentNotes: markPaidNotes || undefined }
            : cf
        ));
        setToastMessage(t('cashflow.payment.markedSuccess', 'Cashflow marked as paid'));
        setToastType('success');
        setShowToast(true);
        closeMarkPaidModal();
      } else {
        setToastMessage(response.message || t('cashflow.payment.markError', 'Failed to mark as paid'));
        setToastType('error');
        setShowToast(true);
      }
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : t('cashflow.payment.markError', 'Failed to mark as paid'));
      setToastType('error');
      setShowToast(true);
    } finally {
      setMarkPaidLoading(false);
    }
  };

  // Unmark cashflow as paid
  const handleUnmarkPaid = async () => {
    if (!clientId || !markPaidCashflow) return;
    setMarkPaidLoading(true);
    try {
      const cfId = (markPaidCashflow as any).id;
      const response = await cashflowService.unmarkCashflowPaid(clientId, cfId);
      if (response.success) {
        setCashflows(prev => prev.map(cf =>
          (cf as any).id === cfId
            ? { ...cf, actualPaymentDate: undefined, paymentMarkedBy: undefined, paymentMarkedAt: undefined, paymentNotes: undefined }
            : cf
        ));
        setToastMessage(t('cashflow.payment.unmarkedSuccess', 'Payment marking removed'));
        setToastType('info');
        setShowToast(true);
        closeMarkPaidModal();
      } else {
        setToastMessage(response.message || t('cashflow.payment.unmarkError', 'Failed to remove payment marking'));
        setToastType('error');
        setShowToast(true);
      }
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : t('cashflow.payment.unmarkError', 'Failed to remove payment marking'));
      setToastType('error');
      setShowToast(true);
    } finally {
      setMarkPaidLoading(false);
    }
  };

  // Initial load — wait for system date before fetching cashflows
  useEffect(() => {
    if (!systemDateResult.systemDate) return;
    loadCashflows();
    loadCurrencies();
  }, [loadCashflows, loadCurrencies, systemDateResult.systemDate]);

  // Extract unique filter options from cashflows
  useEffect(() => {
    const bankAccounts = new Set<string>();
    const counterparties = new Set<string>();
    const ourParties = new Set<string>();
    const settlementTypes = new Set<string>();
    cashflows.forEach((cf) => {
      if (cf.bankAccount) {
        bankAccounts.add(`${cf.bankAccount.bankName} ${cf.bankAccount.accountNumber}`);
      }
      if (cf.counterpartyName) {
        counterparties.add(cf.counterpartyName);
      }
      if (cf.ourParty) {
        ourParties.add(cf.ourParty);
      }
      if (cf.settlementType) {
        settlementTypes.add(cf.settlementType);
      }
    });
    setAvailableBankAccounts(Array.from(bankAccounts).sort());
    setAvailableCounterparties(Array.from(counterparties).sort());
    setAvailableOurParties(Array.from(ourParties).sort());
    setAvailableSettlementTypes(Array.from(settlementTypes).sort());
  }, [cashflows]);

  // Collapse all currency sections by default on initial load (only if no persisted state)
  useEffect(() => {
    if (cashflows.length > 0 && !initialLoadDone.current) {
      initialLoadDone.current = true;
      // Only collapse all if there's no persisted state from a previous session
      if (!hasPersistedState.current) {
        const currencies = new Set(cashflows.map(cf => cf.currency));
        setCollapsedSections(currencies);
        localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(Array.from(currencies)));
      }
    }
  }, [cashflows]);

  // Filter cashflows by currency, bank account, counterparty, direction, and projected status
  const filteredCashflows = useMemo(() => {
    return cashflows.filter((cf) => {
      // Currency filter (if any selected)
      if (currencyFilters.size > 0 && !currencyFilters.has(cf.currency)) {
        return false;
      }
      // Bank account filter (if any selected)
      if (bankAccountFilters.size > 0) {
        if (!cf.bankAccount) return false;
        const bankAccountStr = `${cf.bankAccount.bankName} ${cf.bankAccount.accountNumber}`;
        if (!bankAccountFilters.has(bankAccountStr)) return false;
      }
      // Counterparty filter (if any selected)
      if (counterpartyFilters.size > 0 && !counterpartyFilters.has(cf.counterpartyName)) {
        return false;
      }
      // Our Party filter (if any selected)
      if (ourPartyFilters.size > 0 && !ourPartyFilters.has(cf.ourParty || '')) {
        return false;
      }
      // Settlement Type filter (if any selected)
      if (settlementTypeFilters.size > 0 && !settlementTypeFilters.has(cf.settlementType || '')) {
        return false;
      }
      // Direction filter
      if (directionFilter && cf.flowDirection !== directionFilter) {
        return false;
      }
      // Buy/Sell filter
      if (buySellFilter && cf.direction !== buySellFilter) {
        return false;
      }
      // Projected filter - exclude projected cashflows if not included
      if (!includeProjected && cf.isProjected === true) {
        return false;
      }
      // Paid filter
      if (paidFilter === 'paid' && !cf.actualPaymentDate) {
        return false;
      }
      if (paidFilter === 'unpaid' && cf.actualPaymentDate) {
        return false;
      }
      return true;
    });
  }, [cashflows, currencyFilters, bankAccountFilters, counterpartyFilters, ourPartyFilters, settlementTypeFilters, directionFilter, buySellFilter, includeProjected, paidFilter]);

  // Filter manual cashflows by currency, bank account, direction, and horizon
  const filteredManualCashflows = useMemo(() => {
    // Use system date as horizon start (fall back to browser clock)
    const today = systemDateResult.systemDate
      ? new Date(systemDateResult.systemDate + 'T00:00:00')
      : new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + appliedDays);

    return manualCashflows.filter((cf) => {
      // Currency filter (if any selected)
      if (currencyFilters.size > 0 && !currencyFilters.has(cf.currency)) {
        return false;
      }
      // Bank account filter (if any selected)
      if (bankAccountFilters.size > 0) {
        if (!cf.bankAccount) return false;
        const bankAccountStr = `${cf.bankAccount.bankName} ${cf.bankAccount.accountNumber}`;
        if (!bankAccountFilters.has(bankAccountStr)) return false;
      }
      // Direction filter
      if (directionFilter && cf.flowDirection !== directionFilter) {
        return false;
      }
      // Horizon filter - only show entries within the selected days ahead
      const cfDate = parseDDMMYYYYToDate(cf.expectedDate);
      if (cfDate) {
        if (cfDate < today || cfDate > maxDate) {
          return false;
        }
      }
      return true;
    });
  }, [manualCashflows, currencyFilters, bankAccountFilters, directionFilter, appliedDays, systemDateResult.systemDate]);

  // Compute summary from filtered data (both trade and manual cashflows)
  const filteredSummary = useMemo((): CashflowSummary | null => {
    // Combine filtered trade cashflows and filtered manual cashflows
    const allFiltered = [
      ...filteredCashflows,
      ...filteredManualCashflows.map(mc => ({
        ...mc,
        status: 'final' as const,
      })),
    ];

    if (allFiltered.length === 0) return null;

    const byCurrency: Record<string, { inbound: number; outbound: number; net: number }> = {};
    let finalCount = 0;
    let pendingCount = 0;

    for (const cf of allFiltered) {
      // Initialize currency bucket if needed
      if (!byCurrency[cf.currency]) {
        byCurrency[cf.currency] = { inbound: 0, outbound: 0, net: 0 };
      }

      // Add to inbound or outbound
      if (cf.flowDirection === 'inbound') {
        byCurrency[cf.currency].inbound += cf.amount;
        byCurrency[cf.currency].net += cf.amount;
      } else {
        byCurrency[cf.currency].outbound += cf.amount;
        byCurrency[cf.currency].net -= cf.amount;
      }

      // Count by status
      if (cf.status === 'final') {
        finalCount++;
      } else {
        pendingCount++;
      }
    }

    return {
      byCurrency,
      totalCount: allFiltered.length,
      finalCount,
      pendingCount,
    };
  }, [filteredCashflows, filteredManualCashflows]);

  // Format number with thousands separator
  const formatAmount = (value: number): string => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Column definitions
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: t('cashflow.columns.paid', 'Paid'),
      colId: 'paid',
      flex: 1,
      minWidth: 110,
      sortable: false,
      filter: false,
      valueGetter: () => '',
      cellRenderer: (params: any) => {
        const cf = params.data;
        if (!cf) return '';
        // No payment tracking for projected cashflows
        if (cf.isProjected) return '';
        if (cf.actualPaymentDate) {
          // Paid — show green check + date, clickable
          const dateParts = cf.actualPaymentDate.split('-');
          const formatted = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
          return React.createElement('span', {
            onClick: () => openMarkPaidModal(cf),
            style: {
              color: '#4CAF50',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            },
            title: t('cashflow.payment.clickToView', 'Click to view/unmark'),
          }, [
            React.createElement(Check, { key: 'icon', size: 14 }),
            React.createElement('span', { key: 'date' }, formatted),
          ]);
        }
        // Unpaid — only show button if expected date <= system date
        const sysDate = systemDateResult.systemDate || new Date().toISOString().slice(0, 10);
        if (cf.expectedDate && cf.expectedDate > sysDate) return '';
        return React.createElement('button', {
          onClick: () => openMarkPaidModal(cf),
          style: {
            padding: '2px 8px',
            backgroundColor: 'transparent',
            border: '1px solid #555',
            borderRadius: '4px',
            color: '#b3b3b3',
            fontSize: '11px',
            cursor: 'pointer',
          },
        }, t('cashflow.payment.markPaid', 'Mark Paid'));
      },
    },
    {
      headerName: t('cashflow.columns.tradeNumber', 'Trade #'),
      field: 'tradeNumber',
      flex: 0.7,
      minWidth: 80,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('cashflow.columns.ourParty', 'Entity'),
      field: 'ourParty',
      flex: 1.2,
      minWidth: 100,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('cashflow.columns.counterparty', 'Counterparty'),
      field: 'counterpartyName',
      flex: 1.4,
      minWidth: 110,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('cashflow.columns.bankAccount', 'Bank Account'),
      field: 'bankAccount',
      flex: 1.4,
      minWidth: 120,
      sortable: true,
      filter: true,
      valueGetter: (params: any) => {
        const bankAccount = params.data?.bankAccount;
        if (!bankAccount) return '-';
        return `${bankAccount.bankName} ${bankAccount.accountNumber}`;
      },
    },
    {
      headerName: t('cashflow.columns.productType', 'Type'),
      field: 'productType',
      flex: 1.2,
      minWidth: 110,
      sortable: true,
      filter: true,
      valueGetter: (params: any) => {
        const product = params.data?.productType || '';
        const subType = params.data?.productSubType;
        if (product === 'Forward' && subType) {
          return `Forward (${subType === 'Forward Starting' ? 'Starting' : subType})`;
        }
        return product;
      },
    },
    {
      headerName: t('cashflow.columns.settlementType', 'Settlement'),
      field: 'settlementType',
      flex: 1.1,
      minWidth: 100,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('cashflow.columns.tradeDirection', 'Buy/Sell'),
      field: 'direction',
      flex: 0.6,
      minWidth: 65,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('cashflow.columns.currency', 'Currency'),
      field: 'currency',
      flex: 0.6,
      minWidth: 65,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('cashflow.columns.amount', 'Amount'),
      field: 'amount',
      flex: 1,
      minWidth: 100,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => {
        const value = params.value || 0;
        const cf = params.data;
        if (cf?.isProjected && cf?.estimatedRate) {
          // In simulation mode, amounts are deterministic — no ~ prefix
          const prefix = isSimulationActive ? '' : '~';
          return React.createElement('span', {
            title: `${t('cashflow.projected.estimatedRate', 'Estimated rate')}: ${cf.estimatedRate.toFixed(4)}`,
          }, `${prefix}${formatAmount(value)}`);
        }
        return formatAmount(value);
      },
      cellStyle: { textAlign: 'right' },
    },
    {
      headerName: t('cashflow.columns.direction', 'Direction'),
      field: 'flowDirection',
      flex: 0.7,
      minWidth: 70,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => {
        const direction = params.value;
        const isInbound = direction === 'inbound';
        return React.createElement('span', {
          style: {
            color: isInbound ? '#4CAF50' : '#ff6b6b',
            fontWeight: 500,
          }
        }, isInbound ? t('cashflow.direction.inbound', 'Inbound') : t('cashflow.direction.outbound', 'Outbound'));
      },
    },
    {
      headerName: t('cashflow.columns.expectedDate', 'Expected Date'),
      field: 'expectedDate',
      flex: 1.2,
      minWidth: 115,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => {
        const dateStr = params.value;
        if (!dateStr) return '';
        // Parse YYYY-MM-DD manually to avoid timezone issues
        const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return dateStr;
        const [, year, month, day] = match;
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        const dayName = date.toLocaleDateString('es-CL', { weekday: 'short' });
        const formatted = date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
        return `${dayName} ${formatted}`;
      },
    },
    {
      headerName: t('cashflow.columns.daysToSettlement', 'Days'),
      field: 'daysToSettlement',
      flex: 0.4,
      minWidth: 55,
      sortable: true,
      filter: true,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: t('cashflow.columns.status', 'Status'),
      field: 'status',
      flex: 0.8,
      minWidth: 80,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => {
        const cf = params.data;
        if (cf?.isProjected) {
          return React.createElement('span', {
            style: { color: '#f0ad4e', fontStyle: 'italic' },
          }, t('cashflow.status.projected', 'Projected'));
        }
        const status = params.value;
        const isFinal = status === 'final';
        return React.createElement('span', {
          style: {
            color: isFinal ? '#4CAF50' : '#b3b3b3',
            fontStyle: isFinal ? 'normal' : 'italic',
          }
        }, isFinal ? t('cashflow.status.final', 'Final') : t('cashflow.status.pending', 'Pending'));
      },
    },
    {
      headerName: t('cashflow.columns.workflowState', 'Workflow'),
      field: 'workflowState',
      flex: 0.9,
      minWidth: 90,
      sortable: true,
      filter: true,
    },
  ], [t, systemDateResult.systemDate, isSimulationActive]);

  // Manual cashflows column definitions
  const manualColumnDefs: ColDef[] = useMemo(() => [
    {
      headerName: t('cashflow.columns.expectedDate', 'Date'),
      field: 'expectedDate',
      width: 130,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => {
        const dateStr = params.value;
        if (!dateStr) return '';
        // Parse YYYY-MM-DD manually to avoid timezone issues
        const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return dateStr;
        const [, year, month, day] = match;
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        const dayName = date.toLocaleDateString('es-CL', { weekday: 'short' });
        const formatted = date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
        return `${dayName} ${formatted}`;
      },
    },
    {
      headerName: t('cashflow.manual.description', 'Description'),
      field: 'description',
      width: 180,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('cashflow.columns.currency', 'Currency'),
      field: 'currency',
      width: 80,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('cashflow.columns.amount', 'Amount'),
      field: 'amount',
      width: 120,
      sortable: true,
      filter: true,
      cellRenderer: (params: any): string => {
        const value = params.value || 0;
        const direction = params.data?.flowDirection;
        const prefix = direction === 'outbound' ? '-' : '+';
        return `${prefix}${formatAmount(value)}`;
      },
      cellStyle: (params: any) => ({
        textAlign: 'right',
        color: params.data?.flowDirection === 'inbound' ? '#4CAF50' : '#ff6b6b',
      }),
    },
    {
      headerName: t('cashflow.columns.bankAccount', 'Bank Account'),
      field: 'bankAccount',
      width: 150,
      sortable: true,
      filter: true,
      valueGetter: (params: any) => {
        const bankAccount = params.data?.bankAccount;
        if (!bankAccount) return '-';
        return `${bankAccount.bankName} ${bankAccount.accountNumber}`;
      },
    },
    {
      headerName: '',
      field: 'actions',
      width: 60,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        return React.createElement('button', {
          onClick: () => deleteManualCashflow(params.data.id),
          style: {
            background: 'transparent',
            border: 'none',
            color: '#ff6b6b',
            cursor: 'pointer',
            padding: '4px',
          },
          title: t('common.delete', 'Delete'),
        }, React.createElement(Trash2, { size: 16 }));
      },
    },
  ], [t, deleteManualCashflow]);

  // Row styling for pending, paid, and projected rows
  const getRowStyle = useCallback((params: any): Record<string, string | number> | undefined => {
    if (params.data?.actualPaymentDate) {
      return { backgroundColor: 'rgba(76, 175, 80, 0.08)' };
    }
    if (params.data?.isProjected) {
      return { fontStyle: 'italic', opacity: 0.55 };
    }
    if (params.data?.status === 'pending') {
      return { fontStyle: 'italic', opacity: 0.85 };
    }
    return undefined;
  }, []);



  // Render chart visualizations for each currency
  const renderCharts = () => {
    if (!filteredSummary || filteredCashflows.length === 0) return null;

    // Get currencies from filtered cashflows
    const currenciesInFiltered = new Set(filteredCashflows.map(cf => cf.currency));
    const currencies = sortCurrencies(Array.from(currenciesInFiltered));

    const chartDefs = [
      { id: 'bar', label: t('cashflow.charts.barChart', 'Inflows & Outflows') },
      { id: 'combo', label: t('cashflow.charts.combo', 'Balance') },
      { id: 'sankey', label: t('cashflow.charts.sankey', 'Flow Routing') },
    ];

    return (
      <div style={{ marginBottom: 24 }}>
        {currencies.map((currency) => {
          const isCollapsed = collapsedSections.has(currency);
          const data = filteredSummary?.byCurrency?.[currency];

          return (
            <div key={currency} style={{ marginBottom: 16 }}>
              {/* Title bar with inline summary data */}
              <button
                onClick={() => toggleSection(currency)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#252525',
                  border: '1px solid #333',
                  borderRadius: isCollapsed ? 8 : '8px 8px 0 0',
                  color: '#e0e0e0',
                  fontSize: 15,
                  fontWeight: 500,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#2a2a2a')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#252525')}
              >
                {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                <span>{currency} - {t('cashflow.charts.graphicalView', 'Graphical View')}</span>
                {/* Inline summary figures */}
                {data && (
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 16, fontSize: 12, fontWeight: 400 }}>
                    <span style={{ color: '#4CAF50' }}>▲ +{formatAmount(data.inbound)}</span>
                    <span style={{ color: '#ff6b6b' }}>▼ -{formatAmount(data.outbound)}</span>
                    <span style={{
                      color: data.net >= 0 ? '#4CAF50' : '#ff6b6b',
                      fontWeight: 600,
                    }}>
                      {t('cashflow.summary.net', 'Net')}: {data.net >= 0 ? '+' : ''}{formatAmount(data.net)}
                    </span>
                  </span>
                )}
              </button>
              {!isCollapsed && (
                <div style={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  padding: 12,
                }}>
                  {/* Bookshelf — collapsed charts as vertical spines, expanded slides open left-to-right */}
                  <div style={{ display: 'flex', height: chartHeights[currency] ?? DEFAULT_CHART_HEIGHT }}>
                    {chartDefs.map((c) => {
                      const isExpanded = expandedChart === c.id;

                      return (
                        <div
                          key={c.id}
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            flex: isExpanded ? '1 1 0px' : '0 0 32px',
                            minWidth: isExpanded ? 0 : 32,
                            overflow: 'hidden',
                            transition: 'flex 0.35s ease, min-width 0.35s ease',
                          }}
                        >
                          {/* Spine */}
                          <button
                            onClick={() => toggleExpandedChart(c.id)}
                            title={c.label}
                            style={{
                              flex: '0 0 32px',
                              width: 32,
                              height: '100%',
                              border: 'none',
                              borderRight: isExpanded ? 'none' : '1px solid #333',
                              backgroundColor: isExpanded ? '#007bff' : '#1e1e1e',
                              cursor: 'pointer',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 0,
                              transition: 'background-color 0.25s',
                            }}
                            onMouseEnter={(e) => {
                              if (!isExpanded) e.currentTarget.style.backgroundColor = '#2a2a2a';
                            }}
                            onMouseLeave={(e) => {
                              if (!isExpanded) e.currentTarget.style.backgroundColor = '#1e1e1e';
                            }}
                          >
                            <span style={{
                              writingMode: 'vertical-lr',
                              transform: 'rotate(180deg)',
                              color: isExpanded ? '#fff' : '#ccc',
                              fontSize: isExpanded ? 13 : 12,
                              fontWeight: isExpanded ? 400 : 500,
                              letterSpacing: '0.5px',
                              whiteSpace: 'nowrap',
                              userSelect: 'none',
                              transition: 'color 0.25s',
                            }}>
                              {c.label}
                            </span>
                          </button>
                          {/* Chart content — always rendered, hidden by overflow when collapsed */}
                          <div style={{
                            flex: '1 1 0px',
                            minWidth: 0,
                            backgroundColor: '#1e1e1e',
                            padding: isExpanded ? 8 : 0,
                            opacity: isExpanded ? 1 : 0,
                            transition: 'opacity 0.25s ease 0.1s',
                            overflow: 'hidden',
                          }}>
                            {c.id === 'bar' && (
                              <CashflowBarChart
                                cashflows={filteredCashflows}
                                currency={currency}
                                manualCashflows={manualCashflows}
                                height={(chartHeights[currency] ?? DEFAULT_CHART_HEIGHT) - 16}
                              />
                            )}
                            {c.id === 'combo' && (
                              <CashflowComboChart
                                cashflows={filteredCashflows}
                                currency={currency}
                                manualCashflows={manualCashflows}
                                startingBalance={getStartingBalanceForCurrency(currency)}
                                height={(chartHeights[currency] ?? DEFAULT_CHART_HEIGHT) - 16}
                              />
                            )}
                            {c.id === 'sankey' && (
                              <CashflowSankeyChart
                                cashflows={filteredCashflows}
                                currency={currency}
                                manualCashflows={manualCashflows}
                                height={(chartHeights[currency] ?? DEFAULT_CHART_HEIGHT) - 16}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Drag handle to resize chart height */}
                  <div
                    onMouseDown={(e) => {
                      e.preventDefault();
                      dragRef.current = {
                        currency,
                        startY: e.clientY,
                        startHeight: chartHeights[currency] ?? DEFAULT_CHART_HEIGHT,
                      };
                      document.body.style.cursor = 'row-resize';
                      document.body.style.userSelect = 'none';
                    }}
                    style={{
                      height: 8,
                      cursor: 'row-resize',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: 2,
                    }}
                  >
                    <div style={{
                      width: 40,
                      height: 3,
                      backgroundColor: '#444',
                      borderRadius: 2,
                      transition: 'background-color 0.2s',
                    }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#666')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#444')}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render reference rates collapsible section (horizontal timeline: dates as columns)
  const renderReferenceRates = () => {
    if (!includeProjected || !referenceRates || referenceRates.rateTypes.length === 0) return null;

    // Default collapsed — the toggle adds/removes 'referenceRates_open' as a marker
    const isCollapsed = !collapsedSections.has('referenceRates_open');

    // Build set of actively-used rate lookups for highlighting
    const usedLookupSet = new Set(referenceRates.usedLookups || []);

    // Generate all dates in the horizon range (not just those with data)
    const allDates: string[] = [];
    if (systemDateResult.systemDate) {
      const start = new Date(systemDateResult.systemDate + 'T00:00:00');
      const end = new Date(start);
      end.setDate(end.getDate() + appliedDays);
      const cursor = new Date(start);
      while (cursor <= end) {
        allDates.push(cursor.toISOString().slice(0, 10));
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    // Handle starting to edit a cell
    const startEditing = (rateType: string, dateStr: string, currentValue: number | undefined) => {
      setEditingCell({ rateType, date: dateStr });
      // Show override value if one exists, otherwise the current displayed value
      const overrideVal = rateOverrides[rateType]?.[dateStr];
      const displayVal = overrideVal ?? currentValue;
      setEditingValue(displayVal != null ? String(displayVal) : '');
    };

    // Handle confirming an edit
    const confirmEdit = () => {
      if (!editingCell) return;
      const { rateType, date } = editingCell;
      // Normalize numeric input: support both "950,5" (es-CL) and "950.5" (en-US)
      // If comma is present, treat it as decimal separator (dots are thousands)
      let normalized = editingValue.trim();
      if (normalized.includes(',')) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
      }
      const parsed = parseFloat(normalized);
      if (isNaN(parsed) || parsed <= 0) {
        // Invalid — cancel edit
        setEditingCell(null);
        setEditingValue('');
        return;
      }
      // Save the override
      setRateOverrides(prev => ({
        ...prev,
        [rateType]: {
          ...prev[rateType],
          [date]: parsed,
        },
      }));
      setEditingCell(null);
      setEditingValue('');
    };

    // Handle cancelling an edit
    const cancelEdit = () => {
      setEditingCell(null);
      setEditingValue('');
    };

    // Handle removing a single override (right-click)
    const removeOverride = (rateType: string, dateStr: string) => {
      setRateOverrides(prev => {
        const updated = { ...prev };
        if (updated[rateType]) {
          const { [dateStr]: _, ...rest } = updated[rateType];
          if (Object.keys(rest).length === 0) {
            delete updated[rateType];
          } else {
            updated[rateType] = rest;
          }
        }
        return updated;
      });
    };

    // Apply a mass shift across the timeline for a rate type.
    // Anchor = first date (today's rate stays fixed). Target = shifted value at the last date.
    // Linearly interpolates across all business dates in between.
    const applyShift = (rateType: string, shiftValue: number, mode: 'percent' | 'absolute') => {
      const timeline = referenceRates.rates;

      // Get business dates that have values for this rate type (skips weekends/holidays)
      const datesWithValues = allDates.filter(d => timeline[d]?.[rateType] != null);
      if (datesWithValues.length < 2) return; // Need at least 2 dates to interpolate

      // Anchor = first date in timeline (today's rate stays fixed as the starting point)
      // If the user has manually edited today's rate, use that override as the anchor
      const firstDate = datesWithValues[0];
      const anchorValue = rateOverrides[rateType]?.[firstDate] ?? timeline[firstDate][rateType];

      // Compute target at end of horizon
      let targetValue: number;
      if (mode === 'percent') {
        targetValue = anchorValue * (1 + shiftValue / 100);
      } else {
        targetValue = anchorValue + shiftValue;
      }

      // Linearly interpolate across all business dates from day 1 to end
      const totalSteps = datesWithValues.length - 1;
      const newOverrides: Record<string, number> = {};
      const decimals = rateType === 'bbg_11_am_eur_usd' ? 5 : 2;

      for (let i = 1; i <= totalSteps; i++) {
        const dateStr = datesWithValues[i];
        const progress = i / totalSteps;
        const interpolated = anchorValue + (targetValue - anchorValue) * progress;
        newOverrides[dateStr] = Number(interpolated.toFixed(decimals));
      }

      setRateOverrides(prev => ({
        ...prev,
        [rateType]: {
          ...prev[rateType],
          ...newOverrides,
        },
      }));
      setShiftPopover(null);
      setCustomShiftValue('');
    };

    // Apply custom shift
    const applyCustomShift = (rateType: string) => {
      let normalized = customShiftValue.trim();
      if (normalized.includes(',')) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
      }
      const val = parseFloat(normalized);
      if (isNaN(val)) return;
      applyShift(rateType, val, customShiftMode);
    };

    return (
      <div style={{ marginBottom: 12 }}>
        <button
          onClick={() => toggleSection('referenceRates_open')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            padding: '8px 12px',
            backgroundColor: '#252525',
            border: '1px solid #333',
            borderRadius: isCollapsed ? 8 : '8px 8px 0 0',
            color: '#e0e0e0',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#2a2a2a')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#252525')}
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          {t('cashflow.referenceRates.title', 'Projection Prices')}
          {hasOverrides && (
            <span style={{
              marginLeft: 'auto',
              fontSize: 11,
              color: '#ff9800',
              fontWeight: 400,
            }}>
              {t('cashflow.referenceRates.overridesActive', 'Overrides active')}
            </span>
          )}
        </button>
        {!isCollapsed && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            padding: '8px 4px',
            overflowX: 'auto',
          }}>
            {allDates.length === 0 ? (
              <div style={{ color: '#888', fontSize: 13, textAlign: 'center', padding: 12 }}>
                {t('cashflow.referenceRates.noData', 'No reference rate data available')}
              </div>
            ) : (
              <table style={{
                width: '100%',
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                fontSize: 13,
                color: '#e0e0e0',
              }}>
                <colgroup>
                  <col style={{ width: 100 }} />
                  {allDates.map((d) => <col key={d} />)}
                </colgroup>
                <thead>
                  <tr>
                    <th style={{
                      textAlign: 'left',
                      padding: '5px 8px',
                      borderBottom: '1px solid #444',
                      color: '#b3b3b3',
                      fontWeight: 500,
                    }} />
                    {allDates.map((dateStr) => {
                      const [year, month, day] = dateStr.split('-').map(Number);
                      const dateObj = new Date(year, month - 1, day);
                      const dayName = dateObj.toLocaleDateString(i18n.language || 'es', { weekday: 'narrow' });
                      const dayNum = day.toString();
                      const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                      return (
                        <th key={dateStr} style={{
                          textAlign: 'center',
                          padding: '4px 2px',
                          borderBottom: '1px solid #444',
                          color: isWeekend ? '#555' : '#999',
                          fontWeight: 400,
                          fontSize: 12,
                          lineHeight: '1.3',
                          overflow: 'hidden',
                        }}>
                          <div style={{ fontSize: 10, textTransform: 'uppercase' }}>{dayName}</div>
                          <div>{dayNum}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {referenceRates.rateTypes.map((rt) => {
                    const isShiftOpen = shiftPopover === rt.rateType;
                    // Currency unit for absolute shift labels
                    const unitLabel = rt.rateType === 'bbg_11_am_eur_usd' ? 'USD' : 'CLP';
                    return (
                    <React.Fragment key={rt.rateType}>
                    <tr>
                      <td style={{
                        padding: '5px 8px',
                        borderBottom: isShiftOpen ? 'none' : '1px solid #333',
                        fontWeight: 500,
                        color: '#b3b3b3',
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                        overflow: 'visible',
                        position: 'relative',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{rt.label}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShiftPopover(isShiftOpen ? null : rt.rateType);
                              setCustomShiftValue('');
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: isShiftOpen ? '#ff9800' : '#666',
                              cursor: 'pointer',
                              fontSize: 13,
                              padding: '0 2px',
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                            title={t('cashflow.referenceRates.shiftTimeline', 'Shift timeline')}
                          >
                            ±
                          </button>
                        </div>
                      </td>
                      {allDates.map((dateStr) => {
                        const dayRates = referenceRates.rates[dateStr] || {};
                        const daySources = referenceRates.sources?.[dateStr] || {};
                        const value = dayRates[rt.rateType];
                        const source = daySources[rt.rateType]; // "db" | "filled" | "override" | undefined
                        const isFilled = source === 'filled';
                        const isOverride = source === 'override';
                        const isUsed = usedLookupSet.has(`${rt.rateType}:${dateStr}`);
                        const isEditing = editingCell?.rateType === rt.rateType && editingCell?.date === dateStr;
                        const hasPendingOverride = rateOverrides[rt.rateType]?.[dateStr] != null;

                        // Colour logic: override (orange) > used (blue) > filled (muted) > db (grey)
                        let cellColor = '#3a3a3a'; // no value
                        if (value != null) {
                          if (isOverride || hasPendingOverride) {
                            cellColor = '#ff9800'; // orange for user-edited
                          } else if (isUsed) {
                            cellColor = '#4fc3f7'; // light blue for actively-used
                          } else if (isFilled) {
                            cellColor = '#666';
                          } else {
                            cellColor = '#999';
                          }
                        }

                        // Editable cells: any cell that has a value (or has an override pending)
                        const isEditable = value != null || hasPendingOverride;

                        // Currently editing this cell — show input
                        if (isEditing) {
                          return (
                            <td key={dateStr} style={{
                              textAlign: 'center',
                              padding: '2px 1px',
                              borderBottom: '1px solid #333',
                            }}>
                              <input
                                type="text"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') confirmEdit();
                                  if (e.key === 'Escape') cancelEdit();
                                }}
                                onBlur={confirmEdit}
                                autoFocus
                                style={{
                                  width: '100%',
                                  backgroundColor: '#2a2a2a',
                                  border: '1px solid #ff9800',
                                  borderRadius: 3,
                                  color: '#ff9800',
                                  fontFamily: 'monospace',
                                  fontSize: 12,
                                  textAlign: 'center',
                                  padding: '3px 2px',
                                  outline: 'none',
                                  boxSizing: 'border-box',
                                }}
                              />
                            </td>
                          );
                        }

                        // Display value: show pending override if exists (before recalculate)
                        const displayValue = hasPendingOverride ? rateOverrides[rt.rateType][dateStr] : value;

                        return (
                          <td key={dateStr} style={{
                            textAlign: 'center',
                            padding: '5px 2px',
                            borderBottom: '1px solid #333',
                            fontFamily: 'monospace',
                            fontSize: 12,
                            color: hasPendingOverride ? '#ff9800' : cellColor,
                            fontWeight: (isUsed || isOverride || hasPendingOverride) ? 600 : 400,
                            fontStyle: (isFilled && !isUsed && !hasPendingOverride) ? 'italic' : 'normal',
                            overflow: 'hidden',
                            cursor: isEditable ? 'pointer' : 'default',
                          }}
                          title={
                            hasPendingOverride
                              ? t('cashflow.referenceRates.pendingOverride', 'User override (click Recalculate to apply)')
                              : isOverride
                                ? t('cashflow.referenceRates.appliedOverride', 'User override (applied)')
                                : value != null ? (
                                    isUsed
                                      ? t('cashflow.referenceRates.usedInProjection', 'Used in projection calculation')
                                      : isFilled
                                        ? t('cashflow.referenceRates.filledRate', 'Forward-filled from last known value')
                                        : t('cashflow.referenceRates.publishedRate', 'Published rate')
                                  ) : undefined
                          }
                          onClick={() => {
                            if (isEditable) startEditing(rt.rateType, dateStr, value);
                          }}
                          onContextMenu={(e) => {
                            if (hasPendingOverride || isOverride) {
                              e.preventDefault();
                              removeOverride(rt.rateType, dateStr);
                            }
                          }}
                          >
                            {displayValue != null ? displayValue.toLocaleString('es-CL', {
                              minimumFractionDigits: rt.rateType === 'bbg_11_am_eur_usd' ? 5 : 2,
                              maximumFractionDigits: rt.rateType === 'bbg_11_am_eur_usd' ? 5 : 2,
                            }) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                    {/* Shift popover row — shown below the rate type row when ± is toggled */}
                    {isShiftOpen && (
                      <tr>
                        <td colSpan={allDates.length + 1} style={{
                          padding: '6px 8px',
                          borderBottom: '1px solid #333',
                          backgroundColor: '#222',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ color: '#999', fontSize: 11, marginRight: 4 }}>
                              {t('cashflow.referenceRates.shiftLabel', 'Shift')} {rt.label}:
                            </span>
                            {[
                              { label: '+5%', value: 5, mode: 'percent' as const },
                              { label: '-5%', value: -5, mode: 'percent' as const },
                              { label: `+5 ${unitLabel}`, value: 5, mode: 'absolute' as const },
                              { label: `-5 ${unitLabel}`, value: -5, mode: 'absolute' as const },
                            ].map((preset) => (
                              <button
                                key={preset.label}
                                onClick={() => applyShift(rt.rateType, preset.value, preset.mode)}
                                style={{
                                  padding: '3px 8px',
                                  backgroundColor: '#333',
                                  border: '1px solid #555',
                                  borderRadius: 3,
                                  color: '#e0e0e0',
                                  fontSize: 11,
                                  cursor: 'pointer',
                                }}
                              >
                                {preset.label}
                              </button>
                            ))}
                            <span style={{ color: '#555', margin: '0 2px' }}>|</span>
                            <select
                              value={customShiftMode}
                              onChange={(e) => setCustomShiftMode(e.target.value as 'percent' | 'absolute')}
                              style={{
                                backgroundColor: '#333',
                                border: '1px solid #555',
                                borderRadius: 3,
                                color: '#e0e0e0',
                                fontSize: 11,
                                padding: '3px 4px',
                              }}
                            >
                              <option value="percent">%</option>
                              <option value="absolute">{unitLabel}</option>
                            </select>
                            <input
                              type="text"
                              value={customShiftValue}
                              onChange={(e) => setCustomShiftValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') applyCustomShift(rt.rateType);
                              }}
                              placeholder={t('cashflow.referenceRates.customShift', 'Custom')}
                              style={{
                                width: 60,
                                backgroundColor: '#2a2a2a',
                                border: '1px solid #555',
                                borderRadius: 3,
                                color: '#e0e0e0',
                                fontSize: 11,
                                padding: '3px 6px',
                                textAlign: 'center',
                              }}
                            />
                            <button
                              onClick={() => applyCustomShift(rt.rateType)}
                              style={{
                                padding: '3px 10px',
                                backgroundColor: '#1976d2',
                                border: 'none',
                                borderRadius: 3,
                                color: '#fff',
                                fontSize: 11,
                                cursor: 'pointer',
                              }}
                            >
                              {t('cashflow.referenceRates.apply', 'Apply')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
            {/* Action bar — Recalculate / Reset (below the grid, always visible when overrides exist) */}
            {hasOverrides && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 8,
                padding: '8px 8px 4px',
                borderTop: '1px solid #333',
                marginTop: 4,
              }}>
                <button
                  onClick={resetSimulation}
                  style={{
                    padding: '6px 16px',
                    backgroundColor: 'transparent',
                    border: '1px solid #555',
                    borderRadius: 4,
                    color: '#b3b3b3',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  {t('cashflow.referenceRates.reset', 'Reset')}
                </button>
                <button
                  onClick={runSimulation}
                  disabled={isSimulating}
                  style={{
                    padding: '6px 20px',
                    backgroundColor: isSimulating ? '#555' : '#1976d2',
                    border: 'none',
                    borderRadius: 4,
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: isSimulating ? 'wait' : 'pointer',
                    opacity: isSimulating ? 0.7 : 1,
                  }}
                >
                  {isSimulating
                    ? t('cashflow.referenceRates.simulating', 'Simulating...')
                    : t('cashflow.referenceRates.recalculate', 'Recalculate')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="trade-booking-page">
      {/* Header */}
      <div className="booking-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="booking-title">{t('cashflow.title', 'Cashflow Management')}</div>
        <SystemDateDisplay clientId={clientId} systemDateData={systemDateResult} style={{ marginBottom: 0 }} />
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        marginBottom: '20px',
        flexWrap: 'nowrap',
      }}>
        {/* Left side — all filter groups, wrapping */}
        <div style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          flexWrap: 'wrap',
          flex: 1,
          minWidth: 0,
          rowGap: '10px',
        }}>
        {/* Days selector - slider with snap points and notches */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label style={{ color: '#b3b3b3', fontSize: '14px', whiteSpace: 'nowrap' }}>
            {t('cashflow.horizon', 'Horizon')}:
          </label>
          {/* Slider */}
          <input
            type="range"
            min="1"
            max="30"
            value={daysAhead}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              setDaysAhead(value);
            }}
            style={{
              width: '120px',
              height: '6px',
              cursor: 'pointer',
              accentColor: '#007bff',
            }}
          />
          {/* Editable value display */}
          <input
            type="number"
            min="1"
            max="30"
            value={daysAhead}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value) && value >= 1 && value <= 30) {
                setDaysAhead(value);
              }
            }}
            onBlur={(e) => {
              const value = parseInt(e.target.value, 10);
              if (isNaN(value) || value < 1) {
                setDaysAhead(1);
              } else if (value > 30) {
                setDaysAhead(30);
              }
            }}
            style={{
              width: '45px',
              padding: '2px 4px',
              backgroundColor: '#2a2a2a',
              border: '1px solid #444',
              borderRadius: '4px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 500,
              textAlign: 'center',
            }}
          />
          {/* OK button — only shown when slider differs from applied value */}
          {daysAhead !== appliedDays && (
            <button
              onClick={() => setAppliedDays(daysAhead)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '4px',
                color: '#e2e8f0',
                padding: '2px 10px',
                fontSize: '12px',
                height: '28px',
                cursor: 'pointer',
                whiteSpace: 'nowrap' as const,
              }}
            >
              OK
            </button>
          )}
          {/* Date range label — shows the actual window being queried */}
          {(() => {
            const baseDate = systemDateResult.systemDate || new Date().toISOString().slice(0, 10);
            const from = new Date(baseDate + 'T00:00:00');
            const to = new Date(baseDate + 'T00:00:00');
            to.setDate(to.getDate() + daysAhead);
            const fmt = (d: Date) => d.toLocaleDateString(i18n.language || 'es', { day: 'numeric', month: 'short' });
            return (
              <span style={{ color: '#94a3b8', fontSize: '12px', whiteSpace: 'nowrap' }}>
                {fmt(from)} – {fmt(to)}
              </span>
            );
          })()}
        </div>

        {/* Currency filter - multi-select toggle buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ color: '#b3b3b3', fontSize: '14px' }}>
            {t('cashflow.filters.currency', 'Currency')}:
          </label>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setCurrencyFilters(new Set())}
              style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: currencyFilters.size === 0 ? '#007bff' : '#2a2a2a',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t('cashflow.filters.all', 'All')}
            </button>
            {availableCurrencies.map((currency) => (
              <button
                key={currency}
                onClick={() => toggleCurrencyFilter(currency)}
                style={{
                  padding: '0.3rem 0.6rem',
                  borderRadius: '4px',
                  border: '1px solid #444',
                  backgroundColor: currencyFilters.has(currency) ? '#007bff' : '#2a2a2a',
                  color: '#fff',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {currency}
              </button>
            ))}
          </div>
        </div>

        {/* Direction filter - toggle buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ color: '#b3b3b3', fontSize: '14px' }}>
            {t('cashflow.filters.direction', 'Direction')}:
          </label>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setDirectionFilter('')}
              style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: directionFilter === '' ? '#007bff' : '#2a2a2a',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t('cashflow.filters.all', 'All')}
            </button>
            <button
              onClick={() => setDirectionFilter('inbound')}
              style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: directionFilter === 'inbound' ? '#007bff' : '#2a2a2a',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t('cashflow.direction.inbound', 'Inbound')}
            </button>
            <button
              onClick={() => setDirectionFilter('outbound')}
              style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: directionFilter === 'outbound' ? '#007bff' : '#2a2a2a',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t('cashflow.direction.outbound', 'Outbound')}
            </button>
          </div>
        </div>

        {/* Buy/Sell filter - toggle buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ color: '#b3b3b3', fontSize: '14px' }}>
            {t('cashflow.filters.buySell', 'Buy/Sell')}:
          </label>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setBuySellFilter('')}
              style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: buySellFilter === '' ? '#007bff' : '#2a2a2a',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t('cashflow.filters.all', 'All')}
            </button>
            <button
              onClick={() => setBuySellFilter('Buy')}
              style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: buySellFilter === 'Buy' ? '#007bff' : '#2a2a2a',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t('cashflow.filters.buy', 'Buy')}
            </button>
            <button
              onClick={() => setBuySellFilter('Sell')}
              style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: buySellFilter === 'Sell' ? '#007bff' : '#2a2a2a',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t('cashflow.filters.sell', 'Sell')}
            </button>
          </div>
        </div>

        {/* Paid filter - toggle buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ color: '#b3b3b3', fontSize: '14px' }}>
            {t('cashflow.filters.paid', 'Paid')}:
          </label>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setPaidFilter('')}
              style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: paidFilter === '' ? '#007bff' : '#2a2a2a',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t('cashflow.filters.all', 'All')}
            </button>
            <button
              onClick={() => setPaidFilter('paid')}
              style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: paidFilter === 'paid' ? '#007bff' : '#2a2a2a',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t('cashflow.filters.paidOnly', 'Paid')}
            </button>
            <button
              onClick={() => setPaidFilter('unpaid')}
              style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: paidFilter === 'unpaid' ? '#007bff' : '#2a2a2a',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t('cashflow.filters.unpaidOnly', 'Unpaid')}
            </button>
          </div>
        </div>

        {/* Bank Account filter - toggle buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ color: '#b3b3b3', fontSize: '14px' }}>
            {t('cashflow.filters.bankAccount', 'Bank Account')}:
          </label>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setBankAccountFilters(new Set())}
              style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: bankAccountFilters.size === 0 ? '#007bff' : '#2a2a2a',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t('cashflow.filters.all', 'All')}
            </button>
            {availableBankAccounts.map((bankAccount) => {
              const balance = bankAccountBalances[bankAccount] || 0;
              const currency = bankAccountToCurrency[bankAccount] || '';
              const balanceDisplay = balance !== 0 ? ` (${currency} ${formatAmount(balance)})` : '';
              return (
                <button
                  key={bankAccount}
                  onClick={(e) => {
                    // Only toggle filter on left-click (button 0), not right-click
                    if (e.button === 0) {
                      toggleBankAccountFilter(bankAccount);
                    }
                  }}
                  onContextMenu={(e) => handleBankAccountContextMenu(e, bankAccount)}
                  title={t('cashflow.startingBalances.rightClickHint', 'Right-click to set starting balance')}
                  style={{
                    padding: '0.3rem 0.6rem',
                    borderRadius: '4px',
                    border: '1px solid #444',
                    backgroundColor: bankAccountFilters.has(bankAccount) ? '#007bff' : '#2a2a2a',
                    color: '#fff',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {bankAccount}{balanceDisplay}
                </button>
              );
            })}
          </div>
        </div>

        {/* Our Party filter - toggle buttons */}
        {availableOurParties.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ color: '#b3b3b3', fontSize: '14px' }}>
              {t('cashflow.filters.ourParty', 'Our Party')}:
            </label>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setOurPartyFilters(new Set())}
                style={{
                  padding: '0.3rem 0.6rem',
                  borderRadius: '4px',
                  border: '1px solid #444',
                  backgroundColor: ourPartyFilters.size === 0 ? '#007bff' : '#2a2a2a',
                  color: '#fff',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {t('cashflow.filters.all', 'All')}
              </button>
              {availableOurParties.map((party) => (
                <button
                  key={party}
                  onClick={() => toggleOurPartyFilter(party)}
                  style={{
                    padding: '0.3rem 0.6rem',
                    borderRadius: '4px',
                    border: '1px solid #444',
                    backgroundColor: ourPartyFilters.has(party) ? '#007bff' : '#2a2a2a',
                    color: '#fff',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {party}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Counterparty filter - toggle buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ color: '#b3b3b3', fontSize: '14px' }}>
            {t('cashflow.filters.counterparty', 'Counterparty')}:
          </label>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setCounterpartyFilters(new Set())}
              style={{
                padding: '0.3rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: counterpartyFilters.size === 0 ? '#007bff' : '#2a2a2a',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t('cashflow.filters.all', 'All')}
            </button>
            {availableCounterparties.map((counterparty) => (
              <button
                key={counterparty}
                onClick={() => toggleCounterpartyFilter(counterparty)}
                style={{
                  padding: '0.3rem 0.6rem',
                  borderRadius: '4px',
                  border: '1px solid #444',
                  backgroundColor: counterpartyFilters.has(counterparty) ? '#007bff' : '#2a2a2a',
                  color: '#fff',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {counterparty}
              </button>
            ))}
          </div>
        </div>

        {/* Settlement Type filter - toggle buttons */}
        {availableSettlementTypes.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ color: '#b3b3b3', fontSize: '14px' }}>
              {t('cashflow.filters.settlementType', 'Settlement Type')}:
            </label>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setSettlementTypeFilters(new Set())}
                style={{
                  padding: '0.3rem 0.6rem',
                  borderRadius: '4px',
                  border: '1px solid #444',
                  backgroundColor: settlementTypeFilters.size === 0 ? '#007bff' : '#2a2a2a',
                  color: '#fff',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {t('cashflow.filters.all', 'All')}
              </button>
              {availableSettlementTypes.map((st) => (
                <button
                  key={st}
                  onClick={() => toggleSettlementTypeFilter(st)}
                  style={{
                    padding: '0.3rem 0.6rem',
                    borderRadius: '4px',
                    border: '1px solid #444',
                    backgroundColor: settlementTypeFilters.has(st) ? '#007bff' : '#2a2a2a',
                    color: '#fff',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {st}
                </button>
              ))}
            </div>
          </div>
        )}

        </div>

        {/* Right side — action buttons, pinned right */}
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}>
          {/* Include Projected toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="checkbox"
              id="includeProjected"
              checked={includeProjected}
              onChange={(e) => setIncludeProjected(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <label
              htmlFor="includeProjected"
              style={{ color: '#b3b3b3', fontSize: '12px', cursor: 'pointer' }}
            >
              {t('cashflow.includeProjected', 'Include Projected')}
            </label>
          </div>

          {/* Separator */}
          <div style={{ borderLeft: '1px solid #444', height: '24px' }} />

          {/* Clear Filters button */}
          <button
            onClick={() => {
              setCurrencyFilters(new Set());
              setDirectionFilter('');
              setBuySellFilter('');
              setPaidFilter('');
              setBankAccountFilters(new Set());
              setCounterpartyFilters(new Set());
              setOurPartyFilters(new Set());
              setSettlementTypeFilters(new Set());
              setDaysAhead(30);
              setAppliedDays(30);
              setIncludeProjected(true);
            }}
            style={{
              padding: '0.3rem 0.8rem',
              borderRadius: '4px',
              border: '1px solid var(--border-color)',
              backgroundColor: '#dc3545',
              color: '#fff',
              fontSize: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontWeight: 500,
            }}
          >
            {t('cashflow.filters.clearFilters', 'Clear Filters')}
          </button>

          {/* Refresh button */}
          <button
            onClick={loadCashflows}
            disabled={loading}
            style={{
              padding: '0.3rem 0.8rem',
              borderRadius: '4px',
              border: '1px solid #444',
              backgroundColor: loading ? '#6c757d' : '#28a745',
              color: '#fff',
              fontSize: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? t('cashflow.messages.loading', 'Loading...') : t('common.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {/* Reference Rates (shown when projected cashflows are included) */}
      {renderReferenceRates()}

      {/* Resizable Panel Layout */}
      <PanelGroup
        orientation="vertical"
        style={{ flex: 1, minHeight: 0 }}
      >
        {/* Top Panel — Summary Cards + Charts */}
        <Panel defaultSize={55} minSize={10}>
          <div style={{ height: '100%', overflow: 'auto' }}>
            {(loading || isSimulating) ? (
              <div className="section-loading" style={{ height: '100%' }}>
                <div className="section-spinner" />
              </div>
            ) : filteredCashflows.length === 0 && filteredManualCashflows.length === 0 ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: '#888',
                fontSize: 14,
              }}>
                {t('cashflow.messages.noData', { days: appliedDays, defaultValue: `No upcoming cashflows in the next ${appliedDays} days` })}
              </div>
            ) : (
              <>
                {/* Chart Visualizations (with summary cards embedded) */}
                {renderCharts()}
              </>
            )}
          </div>
        </Panel>

        <ResizeHandle direction="vertical" />

        {/* Bottom Panel — Cashflows Grid */}
        <Panel defaultSize={45} minSize={20}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#252525',
              border: '1px solid #333',
              borderRadius: '8px 8px 0 0',
              color: '#e0e0e0',
              fontSize: 14,
              fontWeight: 500,
            }}>
              {t('cashflow.detailTitle', 'Detalle de Flujos de Caja')}
              <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>({filteredCashflows.length})</span>
            </div>
            <div style={{ flex: 1, border: '1px solid #333', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
              {(loading || isSimulating) ? (
                <div className="section-loading" style={{ height: '100%' }}>
                  <div className="section-spinner" />
                </div>
              ) : error ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  color: '#ff6b6b'
                }}>
                  {t('cashflow.messages.error', 'Error')}: {error}
              </div>
              ) : filteredCashflows.length === 0 ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  color: '#b3b3b3'
                }}>
                  {t('cashflow.messages.noData', { days: appliedDays, defaultValue: `No upcoming cashflows in the next ${appliedDays} days` })}
                </div>
              ) : (
                <div className="ag-theme-alpine-dark" style={{ height: '100%', width: '100%' }}>
                  <AgGridReact
                    localeText={agGridLocale}
                    ref={gridRef}
                    rowData={filteredCashflows}
                    columnDefs={columnDefs}
                    getRowStyle={getRowStyle}
                    getRowId={(params) => params.data.id}
                    pagination={true}
                    paginationPageSize={15}
                    paginationPageSizeSelector={[10, 15, 25, 50]}
                    suppressContextMenu={true}
                    defaultColDef={{
                      sortable: true,
                      filter: true,
                      resizable: true,
                      minWidth: 80,
                    }}
                    sortingOrder={['asc', 'desc']}
                    enableCellChangeFlash={true}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Manual Cashflows Grid — commented out, may revisit later
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            ...
          </div>
          */}
        </Panel>
      </PanelGroup>

      {/* Toast */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={4000}
      />

      {/* Bank Account Balance Context Menu */}
      {balanceMenuAnchor && (
        <>
          {/* Backdrop to close menu on click outside */}
          <div
            onClick={closeBalanceMenu}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
          />
          {/* Menu */}
          <div
            style={{
              position: 'fixed',
              top: balanceMenuAnchor.y,
              left: balanceMenuAnchor.x,
              zIndex: 1000,
              backgroundColor: '#2a2a2a',
              border: '1px solid #444',
              borderRadius: '8px',
              padding: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
              minWidth: '220px',
            }}
          >
            <div style={{ marginBottom: '8px', color: '#e0e0e0', fontSize: '13px', fontWeight: 500 }}>
              {t('cashflow.startingBalances.setBalance', 'Set Starting Balance')}
            </div>
            <div style={{ marginBottom: '8px', color: '#888', fontSize: '12px' }}>
              {balanceMenuAnchor.bankAccount}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#b3b3b3', fontSize: '13px' }}>
                {bankAccountToCurrency[balanceMenuAnchor.bankAccount] || '???'}
              </span>
              <input
                type="text"
                value={editingBalance}
                onChange={handleBalanceInputChange}
                onBlur={handleBalanceInputBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveBalance();
                  if (e.key === 'Escape') closeBalanceMenu();
                }}
                autoFocus
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  backgroundColor: '#1e1e1e',
                  border: '1px solid #444',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '13px',
                  textAlign: 'right',
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
              <button
                onClick={closeBalanceMenu}
                style={{
                  padding: '4px 12px',
                  backgroundColor: '#444',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={saveBalance}
                style={{
                  padding: '4px 12px',
                  backgroundColor: '#007bff',
                  border: 'none',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                {t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Mark Paid Modal */}
      {markPaidCashflow && (
        <>
          <div
            onClick={closeMarkPaidModal}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1000,
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1001,
            backgroundColor: '#1e1e1e',
            border: '1px solid #444',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            minWidth: '380px',
            maxWidth: '440px',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#e0e0e0', fontSize: '16px' }}>
                {markPaidCashflow.actualPaymentDate
                  ? t('cashflow.payment.paymentDetails', 'Payment Details')
                  : t('cashflow.payment.markAsPaid', 'Mark as Paid')}
              </h3>
              <button
                onClick={closeMarkPaidModal}
                style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Cashflow summary */}
            <div style={{
              backgroundColor: '#252525',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#b3b3b3',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
            }}>
              <div><strong style={{ color: '#e0e0e0' }}>{t('cashflow.columns.tradeNumber', 'Trade #')}:</strong> {markPaidCashflow.tradeNumber}</div>
              <div><strong style={{ color: '#e0e0e0' }}>{t('cashflow.columns.counterparty', 'Counterparty')}:</strong> {markPaidCashflow.counterpartyName}</div>
              <div><strong style={{ color: '#e0e0e0' }}>{t('cashflow.columns.amount', 'Amount')}:</strong> {markPaidCashflow.currency} {formatAmount(markPaidCashflow.amount)}</div>
              <div><strong style={{ color: '#e0e0e0' }}>{t('cashflow.columns.expectedDate', 'Expected')}:</strong> {markPaidCashflow.expectedDate?.split('-').reverse().join('-')}</div>
            </div>

            {/* Date input */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', color: '#b3b3b3', fontSize: '13px', marginBottom: '4px' }}>
                {t('cashflow.payment.actualDate', 'Actual Payment Date')}
              </label>
              <input
                type="date"
                value={markPaidDate}
                max={systemDateResult.systemDate || new Date().toISOString().slice(0, 10)}
                onChange={(e) => setMarkPaidDate(e.target.value)}
                disabled={!!markPaidCashflow.actualPaymentDate}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: markPaidCashflow.actualPaymentDate ? '#333' : '#2a2a2a',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  colorScheme: 'dark',
                }}
              />
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#b3b3b3', fontSize: '13px', marginBottom: '4px' }}>
                {t('cashflow.payment.notes', 'Notes')} <span style={{ color: '#666' }}>({t('common.optional', 'optional')})</span>
              </label>
              <textarea
                value={markPaidNotes}
                onChange={(e) => setMarkPaidNotes(e.target.value)}
                disabled={!!markPaidCashflow.actualPaymentDate}
                rows={2}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: markPaidCashflow.actualPaymentDate ? '#333' : '#2a2a2a',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '13px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              {markPaidCashflow.actualPaymentDate ? (
                <>
                  <button
                    onClick={closeMarkPaidModal}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#444',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    {t('common.close', 'Close')}
                  </button>
                  <button
                    onClick={handleUnmarkPaid}
                    disabled={markPaidLoading}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#dc3545',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '13px',
                      cursor: markPaidLoading ? 'not-allowed' : 'pointer',
                      opacity: markPaidLoading ? 0.6 : 1,
                    }}
                  >
                    {markPaidLoading ? '...' : t('cashflow.payment.unmarkPaid', 'Unmark Paid')}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={closeMarkPaidModal}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#444',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={handleMarkPaid}
                    disabled={markPaidLoading || !markPaidDate}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#4CAF50',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '13px',
                      cursor: (markPaidLoading || !markPaidDate) ? 'not-allowed' : 'pointer',
                      opacity: (markPaidLoading || !markPaidDate) ? 0.6 : 1,
                    }}
                  >
                    {markPaidLoading ? '...' : t('cashflow.payment.confirmMarkPaid', 'Mark as Paid')}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CashflowManagement;
